import type Highcharts from 'highcharts';
import { isEqualWith } from 'lodash';
import { useRef } from 'react';

/**
 * Collapse value-equal Highcharts options to a stable object reference, so a
 * re-render that doesn't change the chart's data skips highcharts-react-official's
 * destructive `chart.update` redraw (the per-step "flash" during a Board resize,
 * RSP-6086). A real data change still yields a new reference and redraws.
 *
 * Functions compare equal: event handlers get fresh closures every render, so
 * without this the deep-equality would always bust and the stabilisation be a
 * no-op. A behaviour-only callback change with no accompanying value change is
 * therefore not redrawn — callers needing that must stabilise upstream.
 */
export const useStableChartOptions = (
  options: Highcharts.Options
): Highcharts.Options => {
  const ref = useRef<Highcharts.Options>(options);

  const isValueEqual = isEqualWith(ref.current, options, (a, b) => {
    if (typeof a === 'function' && typeof b === 'function') {
      return true;
    }

    return undefined;
  });

  if (!isValueEqual) {
    ref.current = options;
  }

  return ref.current;
};
